import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";

// Imports componentes
import CrearImpuesto from "../../views/Certificados/ImpuestoValorSelectivo/CrearImpuesto";
import EditarImpuesto from "../../views/Certificados/ImpuestoValorSelectivo/EditarImpuesto";

import CrearProdCertificado from "../../views/Certificados/Productos de certificados/CrearProdCertificado"; 
import CrearCertificadoCliente from "../../views/Certificados/CertificadoCliente/CrearCertificadoCliente"; 

export default function GestionarCertificadoModal({ 
  onClose = () => {}, 
  onFinish = () => {},
  listaClientesPreloaded = [],
  certificadoAEditar = null
}) {
  const [activeTab, setActiveTab] = useState("impuesto");
  
  // Si viene un certificado para editar, lo ponemos de inicio. Si no, null.
  const [certificadoCreado, setCertificadoCreado] = useState(certificadoAEditar);
  
  // Mensaje de feedback visual
  const [msgExito, setMsgExito] = useState("");

  // Estados de éxito pestañas 2 y 3
  const [productoGuardado, setProductoGuardado] = useState(false);
  const [clienteGuardado, setClienteGuardado] = useState(false);
  
  // Tabs habilitados si ya existe el certificado (creado o editando)
  const tabsEnabled = certificadoCreado !== null;

  // Actualizar estado si cambia la prop
  useEffect(() => {
    if(certificadoAEditar) {
        setCertificadoCreado(certificadoAEditar);
    }
  }, [certificadoAEditar]);

  const handleClose = () => {
    onClose();
    // Si se hizo alguna acción importante, recargamos
    if (certificadoCreado) {
      onFinish(); 
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div 
        className="modal-panel-lg h-[90vh] flex flex-col bg-white rounded-lg shadow-xl" 
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* HEADER */}
        <div className="modal-header flex justify-between items-center shrink-0 border-b px-6 py-4">
          <h2 className="section-title text-xl font-bold text-gray-800">
             {certificadoAEditar ? "Editar / Completar Certificado" : "Gestión Integral de Certificado"}
          </h2>
          <button className="modal-close-x text-gray-500 hover:text-gray-700" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        {/* TABS */}
        <div className="px-6 pt-4 border-b shrink-0 bg-gray-50 z-10">
          <div className="flex gap-8">
            <button
              type="button"
              className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === "impuesto"
                  ? "border-green-600 text-green-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("impuesto")}
            >
              1. Datos del Certificado
            </button>

            <button
              type="button"
              disabled={!tabsEnabled}
              className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === "producto"
                  ? "border-green-600 text-green-700"
                  : !tabsEnabled
                  ? "border-transparent text-gray-300 cursor-not-allowed"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => tabsEnabled && setActiveTab("producto")}
            >
              2. Asignar Producto
            </button>

            <button
              type="button"
              disabled={!tabsEnabled}
              className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === "cliente"
                  ? "border-green-600 text-green-700"
                  : !tabsEnabled
                  ? "border-transparent text-gray-300 cursor-not-allowed"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => tabsEnabled && setActiveTab("cliente")}
            >
              3. Asignar Clientes
            </button>
          </div>
        </div>

        {/* BODY (Scrollable) */}
        <div className="flex-1 overflow-y-auto bg-white relative">
            
          {/* === PESTAÑA 1: DATOS (CREAR O EDITAR) === */}
          {activeTab === "impuesto" && (
            <div className="p-8 max-w-4xl mx-auto">
              
              {msgExito && (
                <div className="mb-6 p-4 bg-green-50 text-green-800 border border-green-200 rounded-md text-sm flex items-center gap-2">
                   <span>✅</span>
                   <span>{msgExito}</span>
                </div>
              )}
              
              {certificadoAEditar ? (
                  // MODO EDICIÓN
                  <EditarImpuesto 
                    embedded={true}
                    initial={certificadoAEditar}
                    onClose={() => {}}
                    onSaved={(actualizado) => {
                        setCertificadoCreado(actualizado);
                        setMsgExito(`Certificado ${actualizado.numero} actualizado correctamente.`);
                        // En modo edición no saltamos automáticamente, el usuario decide si ir a productos
                    }}
                  />
              ) : (
                  // MODO CREACIÓN
                  <CrearImpuesto 
                    embedded={true} 
                    onClose={() => {}} 
                    onCreated={(nuevoCert) => {
                        setCertificadoCreado(nuevoCert);
                        // Al crear, sí pasamos automáticamente para facilitar flujo
                        setActiveTab("producto"); 
                    }}
                  />
              )}
            </div>
          )}

          {/* === PESTAÑA 2: ASIGNAR PRODUCTO === */}
          {activeTab === "producto" && (
            <div className="p-8 max-w-4xl mx-auto">
              {productoGuardado && (
                <div className="mb-6 p-4 bg-green-50 text-green-800 border border-green-200 rounded-md text-sm flex items-center gap-2 animate-pulse">
                   <span>✅</span>
                   <span>Producto agregado. Puede seguir agregando.</span>
                </div>
              )}

              <div className="mb-4 text-sm text-gray-500">
                Agregando productos al certificado: <strong>{certificadoCreado?.numero}</strong>
              </div>

              {/* CrearProdCertificado siempre es "Agregar nuevo detalle", válido para crear y editar */}
              <CrearProdCertificado 
                embedded={true}
                onClose={() => {}}
                certificadoPredefinido={certificadoCreado}
                onCreated={() => {
                    setProductoGuardado(true);
                    setTimeout(() => setProductoGuardado(false), 3000);
                }}
              />
            </div>
          )}

          {/* === PESTAÑA 3: ASIGNAR CLIENTES === */}
          {activeTab === "cliente" && (
            <div className="p-8 max-w-4xl mx-auto">
              
              {clienteGuardado && (
                <div className="mb-6 p-4 bg-green-50 text-green-800 border border-green-200 rounded-md text-sm flex items-center gap-2">
                   <span>✅</span>
                   <span>Clientes asignados. Cerrando...</span>
                </div>
              )}

              <div className="mb-4 text-sm text-gray-500">
                Asignando clientes al certificado: <strong>{certificadoCreado?.numero}</strong>
              </div>
              
              <CrearCertificadoCliente
                 embedded={true}
                 onClose={() => {}}
                 certificadoPredefinido={certificadoCreado}
                 listaClientesPreloaded={listaClientesPreloaded} 
                 onCreated={() => {
                    setClienteGuardado(true);
                    onFinish(); 
                    onClose(); 
                 }}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}